"use client";

import { Building2, GraduationCap, ShieldCheck, Users, Volleyball, type LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils";

export type LoginRole = "company" | "school" | "member" | "guard" | "consumer";

interface RoleOption {
  id: LoginRole;
  label: string;
  description: string;
  icon: LucideIcon;
}

export const ROLE_OPTIONS: RoleOption[] = [
  { id: "company", label: "Company Admin", description: "Platform owner", icon: Building2 },
  { id: "school", label: "School Admin", description: "Run your school", icon: GraduationCap },
  { id: "member", label: "School Member", description: "Teacher / Student / Staff / Parent", icon: Users },
  { id: "guard", label: "Security Guard", description: "Gate verification", icon: ShieldCheck },
  { id: "consumer", label: "Consumer / Player", description: "Book & play", icon: Volleyball },
];

export function RoleSelector({ value, onChange }: { value: LoginRole; onChange: (r: LoginRole) => void }) {
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-2">
      {ROLE_OPTIONS.map((opt) => {
        const selected = value === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onChange(opt.id)}
            aria-pressed={selected}
            className={cn(
              "flex min-h-[44px] items-center gap-2.5 rounded-xl border p-3 text-left transition-all",
              selected
                ? "border-primary bg-primary/5 shadow-sm ring-1 ring-primary"
                : "border-border bg-card hover:border-primary/40 hover:bg-muted/50"
            )}
          >
            <div
              className={cn(
                "flex size-9 shrink-0 items-center justify-center rounded-lg",
                selected ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
              )}
            >
              <opt.icon className="size-4.5" />
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold leading-tight">{opt.label}</p>
              <p className="truncate text-xs text-muted-foreground">{opt.description}</p>
            </div>
          </button>
        );
      })}
    </div>
  );
}
